import { skip } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { DataTableFilterService, FilterObj, ListTableService } from 'ziti-console-lib';

/** A row a local list can search by name and highlight. */
export interface LocalRow {
    id: string;
    name?: string;
    selected?: boolean;
}

/**
 * Pages, searches and highlights a list that is read whole, for a lib-list-table with the numbered pager. The search
 * and the paging happen here, so only a refresh goes back to the service.
 */
export class LocalListTable<T extends LocalRow> {
    items: T[] = [];
    /** The rows of the current page, as the table draws them. */
    rows: T[] = [];
    selectedId = '';
    filterApplied = false;

    private query = '';
    private page = 1;

    constructor(
        private filterService: DataTableFilterService,
        private tableId: string,
        private defaultPageSize: number,
        private searchColumn: string,
        private refresh: () => void
    ) {}

    /** The items the search keeps, across every page. */
    get visible(): T[] {
        if (!this.query) {
            return this.items;
        }
        return this.items.filter((item) => (item.name || item.id || '').toLowerCase().includes(this.query));
    }

    get startCount(): number {
        return this.rows.length ? (this.page - 1) * this.filterService.pageSize + 1 : 0;
    }

    get endCount(): number {
        return this.rows.length ? (this.page - 1) * this.filterService.pageSize + this.rows.length : 0;
    }

    get totalCount(): number {
        return this.visible.length;
    }

    get hasPrev(): boolean {
        return this.page > 1;
    }

    get hasNext(): boolean {
        return this.page * this.filterService.pageSize < this.visible.length;
    }

    /** The open row, or undefined. Only a row on this page can be open. */
    get selected(): T | undefined {
        return this.rows.find((row) => row.id === this.selectedId);
    }

    get selectedIndex(): number {
        return this.rows.findIndex((row) => row.id === this.selectedId);
    }

    /** Wire the list to the table's search, pager and Refresh. Call once, in ngOnInit. */
    connect(): Subscription {
        const subscription = new Subscription();
        // Read the saved page size now, because the table restores it only in its own ngOnInit, after the first page is cut.
        this.filterService.pageSize = ListTableService.readPersistedPageSize(this.tableId, this.defaultPageSize);
        // Restores the search of a shared URL: clearFilters re-reads the `f.*` parameters.
        this.filterService.clearFilters();
        subscription.add(
            this.filterService.filtersChanged.subscribe((filters) => {
                this.query = searchText(filters, this.searchColumn);
                this.filterApplied = !!this.query;
                this.goToFirstPage();
            })
        );
        // Refresh re-emits the page the table is on, so the same page reads the list again and another page is only a cut.
        subscription.add(
            this.filterService.pageChanged.pipe(skip(1)).subscribe((page) => {
                const next = Number(page) || 1;
                if (next === this.page) {
                    this.refresh();
                } else {
                    this.page = next;
                    this.render();
                }
            })
        );
        subscription.add(this.filterService.pageSizeChanged.pipe(skip(1)).subscribe(() => this.goToFirstPage()));
        return subscription;
    }

    setItems(items: T[]) {
        this.items = items;
        this.render();
    }

    /** Open `row`, or close it when it is the one already open. */
    toggle(row: T) {
        this.selectedId = this.selectedId === row.id ? '' : row.id;
        this.markSelected();
    }

    close() {
        this.selectedId = '';
        this.markSelected();
    }

    /** Move the open row through the rows on this page. */
    step(step: -1 | 1) {
        const next = this.rows[this.selectedIndex + step];
        if (next) {
            this.selectedId = next.id;
            this.markSelected();
        }
    }

    private goToFirstPage() {
        this.page = 1;
        this.filterService.currentPage = 1;
        this.render();
    }

    /** Cut the current page. A page past the end, such as after a delete, falls back to the last page there is. */
    private render() {
        const size = this.filterService.pageSize;
        const lastPage = Math.max(1, Math.ceil(this.visible.length / size));
        if (this.page > lastPage) {
            this.page = lastPage;
            this.filterService.currentPage = lastPage;
        }
        this.rows = this.visible.slice((this.page - 1) * size, this.page * size);
        this.markSelected();
    }

    /** Draw the highlight on the open row, and close it when its row is no longer on the page. */
    private markSelected() {
        if (this.selectedId && !this.selected) {
            this.selectedId = '';
        }
        this.rows.forEach((row) => (row.selected = row.id === this.selectedId));
    }
}

/** The search box's text, lower-cased, or '' when it is empty. */
function searchText(filters: FilterObj[], column: string): string {
    const value = filters.find((item) => item.columnId === column)?.value;
    return typeof value === 'string' ? value.trim().toLowerCase() : '';
}
